/**
 * Normalize Airtable / Softr pitch-deck text into clean Markdown for RichText.
 * Source fields mix markdown (**bold**, - lists) with literal HTML (<br/>) and
 * messy whitespace — react-markdown escapes HTML, so <br/> shows as text otherwise.
 */
export function normalizePitchText(input: string): string {
  let s = input.replace(/\r\n/g, "\n");

  // Common entities from Airtable rich text
  s = s
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Block closers → paragraph breaks before stripping tags
  s = s.replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n\n");
  // Line breaks (the main leak in presentation view)
  s = s.replace(/<br\s*\/?>/gi, "\n");
  // Drop remaining tags; keep inner text
  s = s.replace(/<\/?[a-zA-Z][^>]*>/g, "");

  // Scientific-name italics that nest underscores: _Genus(_abbrev_)_ → *Genus (abbrev)*
  s = s.replace(/_([^_\n(]+)\(_([^_\n]+)_\)_/g, "*$1 ($2)*");
  // Simple _italic_ → *italic* (avoids nested-underscore confusion)
  s = s.replace(/(^|[\s(])_([^_\n]+?)_([\s).,;:!?/-]|$)/g, "$1*$2*$3");

  // Normalize bullet markers
  s = s.replace(/^[ \t]*[•●▪◦]\s+/gm, "- ");
  s = s.replace(/^[ \t]*[-–—]\s+/gm, "- ");

  // Trim trailing spaces; collapse blank runs
  s = s.replace(/[ \t]+$/gm, "");
  s = s.replace(/\n{3,}/g, "\n\n");
  s = s.trim();

  return bulletizeFragmentedClaims(s);
}

/** Turn br-separated short claim fragments into a markdown bullet list. */
function bulletizeFragmentedClaims(s: string): string {
  if (!s) return s;
  // Already a markdown list
  if (/^(\s*[-*+]|\s*\d+\.)\s+/m.test(s)) return s;

  const parts = s
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length < 2) return s;
  // Multi-line blocks are real paragraphs — leave alone
  if (parts.some((p) => p.includes("\n"))) return s;

  const avg = parts.reduce((a, p) => a + p.length, 0) / parts.length;
  // Long prose paragraphs stay as paragraphs
  if (avg > 280) return s;
  if (parts.length === 2 && parts.every((p) => p.length > 160)) return s;

  return parts.map((p) => `- ${p}`).join("\n");
}
